import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useConnectionsStore } from "@/store/connectionsStore";
import { captureException } from "@/lib/analytics";
import { broadcastOAuthConnection } from "@/lib/oauthBroadcast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle2, Loader } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { OAUTH_PROVIDERS } from "@forgepoint/shared";

type CallbackState = "loading" | "success" | "error";

/**
 * Generic OAuth callback page for any supported provider. Reads the provider
 * key from the URL (e.g. `/auth/hubspot/callback`), extracts the authorization
 * code and CSRF state from the query string, and finalizes the connection via
 * the backend, which validates and consumes the state.
 */
export const OAuthCallback = () => {
  const { provider } = useParams<{ provider: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const finalizeConnection = useConnectionsStore(
    (state) => state.finalizeConnection,
  );
  const [callbackState, setCallbackState] = useState<CallbackState>("loading");
  const [error, setError] = useState<string | null>(null);
  const hasProcessed = useRef(false);

  const config = provider ? OAUTH_PROVIDERS[provider] : undefined;

  useEffect(() => {
    if (hasProcessed.current) {
      return;
    }
    hasProcessed.current = true;

    if (!config) {
      setError(`Unknown OAuth provider: ${provider}`);
      setCallbackState("error");
      return;
    }

    const processCallback = async () => {
      const code = searchParams.get("code");
      const state = searchParams.get("state");

      if (!code) {
        setError(`No authorization code received from ${config.displayName}.`);
        setCallbackState("error");
        return;
      }

      try {
        const connection = await finalizeConnection({
          connectionProviderId: config.connectionProviderId,
          appId: config.appId,
          authId: code,
          state: state ?? undefined,
        });

        if (connection) {
          broadcastOAuthConnection({
            providerId: config.connectionProviderId,
            connectionId: connection.id,
          });
          setCallbackState("success");
          // This tab was opened solely for the OAuth flow; close it so the user
          // returns to the originating tab, which has already been notified.
          setTimeout(() => window.close(), 1200);
        } else {
          setError(`Failed to finalize ${config.displayName} connection.`);
          setCallbackState("error");
        }
      } catch (err) {
        captureException(err as Error);
        setError(
          `An error occurred while connecting to ${config.displayName}.`,
        );
        setCallbackState("error");
      }
    };

    processCallback();
  }, [searchParams, finalizeConnection, navigate, config, provider]);

  const displayName = config?.displayName ?? provider ?? "provider";

  return (
    <div className="flex items-center justify-center h-screen bg-background">
      <AnimatePresence mode="wait">
        {callbackState === "loading" && (
          <motion.div
            key="loading"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="text-center space-y-5"
          >
            <div className="flex justify-center">
              <Loader className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">
              Connecting to {displayName}
            </p>
          </motion.div>
        )}

        {callbackState === "success" && (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="text-center space-y-3"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 20,
                delay: 0.1,
              }}
              className="flex justify-center"
            >
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </motion.div>
            <p className="text-sm font-medium text-foreground">
              {displayName} connected
            </p>
          </motion.div>
        )}

        {callbackState === "error" && (
          <motion.div
            key="error"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-sm space-y-4"
          >
            <Alert variant="destructive">
              <AlertTitle className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                Connection failed
              </AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <div className="flex justify-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/settings")}
              >
                Return to Settings
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
